/**
 * COMPATIBILITY of the Publish workspace (W2-09 publish-import; DESIGN.md §4.16): how Phira loads the chart (blocked,
 * plays differently from the preview, or renders as previewed with notes such as baked speed and shared Bezier
 * keys) and what RPE 1.6+ shows differently. The RPE row carries an "Unverified" badge until the RPE-compatible
 * profile has been checked on a real RPE install (DESIGN.md §12.1).
 */

import type { IconName } from '../../app/registry/types';
import { Badge } from '../../ui/Badge';
import { Icon } from '../../ui/Icon';
import { Section } from '../../ui/Section';
import type { Text } from './hooks';
import type { CompatReport, CompatRow } from './preflight';
import { m } from './publish.messages';
import s from './Publish.module.css';

const STATUS_ICON: Readonly<Record<CompatRow['status'], IconName>> = {
  ok: 'CircleCheck',
  notes: 'CircleCheck',
  differs: 'TriangleAlert',
  blocked: 'OctagonX',
};

function phiraStatus(text: Text, row: CompatRow): string {
  switch (row.status) {
    case 'blocked':
      return text.t(m.phiraBlockedStatus);
    case 'differs':
      return text.t(m.phiraDiffers);
    case 'notes':
      return row.approxEffects !== undefined && row.approxEffects > 0
        ? text.t(m.phiraOkExceptEffects, { count: row.approxEffects })
        : text.t(m.phiraOkNotes);
    case 'ok':
      return text.t(m.phiraOk);
  }
}

function Notes(props: { readonly text: Text; readonly row: CompatRow; readonly label: string }) {
  if (props.row.notes.length === 0) return null;
  return (
    <ul className={s.noteList} aria-label={props.label}>
      {props.row.notes.map((n, i) => (
        <li key={`${n.key}:${String(i)}`}>{props.text.tr(n)}</li>
      ))}
    </ul>
  );
}

export function Compatibility(props: { readonly text: Text; readonly report: CompatReport }) {
  const { text, report } = props;
  const phiraName = text.t(m.phiraName);
  const rpeName = text.t(m.rpeName);
  return (
    <Section title={text.t(m.compatTitle)} level={2}>
      <dl className={s.compatList}>
        <div className={s.compatRow} data-target="phira">
          <dt className={s.compatName}>{phiraName}</dt>
          <dd className={s.compatValue}>
            <span className={s.compatStatus} data-status={report.phira.status}>
              <Icon name={STATUS_ICON[report.phira.status]} size={16} />
              {phiraStatus(text, report.phira)}
            </span>
            <Notes text={text} row={report.phira} label={phiraName} />
          </dd>
        </div>
        <div className={s.compatRow} data-target="rpe">
          <dt className={s.compatName}>{rpeName}</dt>
          <dd className={s.compatValue}>
            <span className={s.compatStatus} data-status={report.rpe.status}>
              <Icon name={STATUS_ICON[report.rpe.status]} size={16} />
              {report.rpe.notes.length === 0
                ? text.t(m.rpeOk)
                : text.t(m.rpeOkNotes, { count: report.rpe.notes.length })}
              {!report.rpe.verified && (
                <Badge kind="problem" severity="tip" label={{ key: m.unverified }} tooltip={{ key: m.unverifiedTip }} />
              )}
            </span>
            <Notes text={text} row={report.rpe} label={rpeName} />
          </dd>
        </div>
      </dl>
    </Section>
  );
}
